import moment from 'moment-timezone';

import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {
  addTimezoneEndOfDay,
  addTimezoneStartOfDay,
  getSubtractedDate,
  getTodayInTimezone,
} from '@spaced-out/ui-design-system/lib/utils';
import {getDateRangeDataByOption} from 'src/utils/analytics-genesis';

import {
  DATA_AUDIT_API_DATETIME_FORMAT,
  DATA_AUDIT_MAX_LOOKBACK_DAYS,
} from './constants';


function formatDataAuditApiDateTime(
  date: string,
  timezone: string,
  boundary: 'start' | 'end',
): string {
  const momentInTimezone = moment.tz(date, timezone);

  return boundary === 'start'
    ? momentInTimezone.startOf('day').format(DATA_AUDIT_API_DATETIME_FORMAT)
    : momentInTimezone.endOf('day').format(DATA_AUDIT_API_DATETIME_FORMAT);
}

export function getDataAuditDefaultDateRange(
  timezone: string,
): DateRangeWithTimezone {
  return getDateRangeDataByOption('thirtyDays', timezone);
}

export function getDataAuditMinSelectableDate(timezone: string): string {
  const today = getTodayInTimezone(timezone);

  return getSubtractedDate(today, DATA_AUDIT_MAX_LOOKBACK_DAYS - 1, 'd');
}

export function clampDataAuditDateRange(
  dateRange: DateRangeWithTimezone,
  timezone: string,
): DateRangeWithTimezone {
  const minDate = getDataAuditMinSelectableDate(timezone);
  const today = getTodayInTimezone(timezone);

  let startDate = dateRange.startDate ?? minDate;
  let endDate = dateRange.endDate ?? today;

  if (startDate < minDate) {
    startDate = minDate;
  }

  if (endDate > today) {
    endDate = today;
  }

  if (startDate > endDate) {
    return getDataAuditDefaultDateRange(timezone);
  }

  const earliestStartForEndDate = getSubtractedDate(
    endDate,
    DATA_AUDIT_MAX_LOOKBACK_DAYS - 1,
    'd',
  );

  if (startDate < earliestStartForEndDate) {
    startDate = earliestStartForEndDate;
  }

  if (
    startDate === dateRange.startDate &&
    endDate === dateRange.endDate &&
    timezone === dateRange.timezone
  ) {
    return dateRange;
  }

  return {
    startDate,
    endDate,
    startDateUTC: addTimezoneStartOfDay(startDate, timezone),
    endDateUTC: addTimezoneEndOfDay(endDate, timezone),
    timezone,
  };
}

export function wasDataAuditDateRangeAdjusted(
  dateRange: DateRangeWithTimezone,
  clampedDateRange: DateRangeWithTimezone,
): boolean {
  return (
    clampedDateRange.startDate !== dateRange.startDate ||
    clampedDateRange.endDate !== dateRange.endDate
  );
}

export function getDataAuditDateRangeBounds(dateRange: DateRangeWithTimezone): {
  from: string;
  to: string;
} {
  const timezone = dateRange.timezone ?? '';
  const clampedDateRange = clampDataAuditDateRange(
    {...dateRange, timezone},
    timezone,
  );
  const {startDate, endDate} = clampedDateRange;

  return {
    from: startDate
      ? formatDataAuditApiDateTime(startDate, timezone, 'start')
      : '',
    to: endDate ? formatDataAuditApiDateTime(endDate, timezone, 'end') : '',
  };
}

export function buildDataAuditGraphQuery(input: {
  from?: string;
  to?: string;
}): Record<string, string> {
  const query: Record<string, string> = {};

  if (input.from) {
    query.from = input.from;
  }

  if (input.to) {
    query.to = input.to;
  }

  return query;
}
